import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, UserCheck, UserX, Shield } from 'lucide-react';
import GlassCard from '../../../components/shared/GlassCard';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'internal',
    phone: '',
    department: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const roles = [
    { value: 'super_admin', label: 'Super Admin', color: '#570683' },
    { value: 'program_admin', label: 'Program Admin', color: '#417E46' },
    { value: 'noc_operator', label: 'NOC Operator', color: '#0DA34E' },
    { value: 'fleet_manager', label: 'Fleet Manager', color: '#DF8C4D' },
    { value: 'finance_viewer', label: 'Finance Viewer', color: '#4A90E2' },
    { value: 'gov_viewer', label: 'Gov Viewer', color: '#9B59B6' },
    { value: 'internal', label: 'Internal', color: '#7F8C8D' },
    { value: 'guest', label: 'Guest', color: '#95A5A6' }
  ];

  const statuses = [
    { value: 'active', label: 'Active', color: '#0DA34E' },
    { value: 'inactive', label: 'Inactive', color: '#95A5A6' },
    { value: 'suspended', label: 'Suspended', color: '#E74C3C' },
    { value: 'pending', label: 'Pending', color: '#F39C12' }
  ];

  useEffect(() => {
    fetchUsers();
  }, [filterRole, filterStatus]);

  const fetchUsers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (filterRole) params.append('role', filterRole);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await fetch(`${backendUrl}/api/rbac/users?${params}`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin'
        }
      });
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setFormData({
      email: '',
      name: '',
      role: 'internal',
      phone: '',
      department: '',
      password: ''
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      phone: user.phone || '',
      department: user.department || '',
      status: user.status
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const validateForm = (isEdit = false) => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!isEdit && !formData.email.trim()) errors.email = 'Email is required';
    if (!isEdit && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.email = 'Invalid email format';
    if (!isEdit && !formData.password) errors.password = 'Password is required';
    if (!isEdit && formData.password && formData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitAdd = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': localStorage.getItem('userRole') || 'super_admin',
          'x-user-id': localStorage.getItem('userId') || 'system',
          'x-user-email': localStorage.getItem('userEmail') || 'system@urgaa.in',
          'x-user-name': localStorage.getItem('userName') || 'System'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setShowAddModal(false);
        fetchUsers();
        alert('User created successfully!');
      } else {
        alert(data.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!validateForm(true)) return;
    
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': localStorage.getItem('userRole') || 'super_admin',
          'x-user-id': localStorage.getItem('userId') || 'system',
          'x-user-email': localStorage.getItem('userEmail') || 'system@urgaa.in',
          'x-user-name': localStorage.getItem('userName') || 'System'
        },
        body: JSON.stringify({
          name: formData.name,
          role: formData.role,
          phone: formData.phone,
          department: formData.department,
          status: formData.status
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setShowEditModal(false);
        fetchUsers();
        alert('User updated successfully!');
      } else {
        alert(data.detail || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/rbac/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin',
          'x-user-id': localStorage.getItem('userId') || 'system',
          'x-user-email': localStorage.getItem('userEmail') || 'system@urgaa.in',
          'x-user-name': localStorage.getItem('userName') || 'System'
        }
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getRoleBadgeColor = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj?.color || '#7F8C8D';
  };

  const getStatusBadgeColor = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj?.color || '#95A5A6';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text)' }}>
          User Management
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Manage users, roles, and permissions
        </p>
      </div>

      {/* Actions Bar */}
      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        {/* Search */}
        <div className="flex-1 min-w-[300px] max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted)' }} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border"
            style={{ 
              backgroundColor: 'var(--panel-bg)',
              borderColor: 'var(--panel-border)',
              color: 'var(--text)'
            }}
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 rounded-lg border"
            style={{ 
              backgroundColor: 'var(--panel-bg)',
              borderColor: 'var(--panel-border)',
              color: 'var(--text)'
            }}
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 rounded-lg border"
            style={{ 
              backgroundColor: 'var(--panel-bg)',
              borderColor: 'var(--panel-border)',
              color: 'var(--text)'
            }}
          >
            <option value="">All Statuses</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>

        {/* Add User Button */}
        <button
          onClick={handleAddUser}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors"
          style={{ 
            backgroundColor: 'var(--g4g-accent)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Users Table */}
      <GlassCard>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-transparent"></div>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <UserX className="w-12 h-12 mx-auto mb-4 opacity-40" style={{ color: 'var(--muted)' }} />
            <p className="text-lg font-medium mb-2" style={{ color: 'var(--text)' }}>No users found</p>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {searchQuery || filterRole || filterStatus ? 'Try adjusting your filters' : 'Add your first user to get started'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--panel-border)' }}>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>User</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Role</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Department</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Last Login</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr 
                    key={user.id} 
                    className="border-b hover:bg-gray-50/50 transition-colors"
                    style={{ borderColor: 'var(--panel-border)' }}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium" style={{ color: 'var(--text)' }}>{user.name}</div>
                        <div className="text-xs" style={{ color: 'var(--muted)' }}>{user.email}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getRoleBadgeColor(user.role)}15`,
                          color: getRoleBadgeColor(user.role)
                        }}
                      >
                        <Shield className="w-3 h-3" />
                        {roles.find(r => r.value === user.role)?.label || user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getStatusBadgeColor(user.status)}15`,
                          color: getStatusBadgeColor(user.status)
                        }}
                      >
                        {user.status === 'active' ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                        {statuses.find(s => s.value === user.status)?.label || user.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm" style={{ color: 'var(--text)' }}>
                        {user.department || '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm" style={{ color: 'var(--muted)' }}>
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="Edit user"
                        >
                          <Edit2 className="w-4 h-4" style={{ color: 'var(--g4g-accent)' }} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="Deactivate user"
                        >
                          <Trash2 className="w-4 h-4" style={{ color: '#E74C3C' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
          <div className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{users.length}</div>
          <div className="text-sm" style={{ color: 'var(--muted)' }}>Total Users</div>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
          <div className="text-2xl font-bold" style={{ color: '#0DA34E' }}>
            {users.filter(u => u.status === 'active').length}
          </div>
          <div className="text-sm" style={{ color: 'var(--muted)' }}>Active Users</div>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
          <div className="text-2xl font-bold" style={{ color: '#570683' }}>
            {users.filter(u => u.role === 'super_admin' || u.role === 'program_admin').length}
          </div>
          <div className="text-sm" style={{ color: 'var(--muted)' }}>Admins</div>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
          <div className="text-2xl font-bold" style={{ color: '#F39C12' }}>
            {users.filter(u => u.status === 'pending').length}
          </div>
          <div className="text-sm" style={{ color: 'var(--muted)' }}>Pending</div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>Add New User</h3>
              
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: formErrors.name ? '#E74C3C' : 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                    placeholder="Enter full name"
                  />
                  {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: formErrors.email ? '#E74C3C' : 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                    placeholder="user@example.com"
                  />
                  {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: formErrors.password ? '#E74C3C' : 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                    placeholder="Minimum 8 characters"
                  />
                  {formErrors.password && <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>}
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                    placeholder="+91 1234567890"
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                    placeholder="e.g., Operations, Finance"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{ 
                    borderColor: 'var(--panel-border)',
                    color: 'var(--text)'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitAdd}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg font-medium"
                  style={{ 
                    backgroundColor: 'var(--g4g-accent)',
                    color: 'white'
                  }}
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
                Edit User: {selectedUser.name}
              </h3>
              
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: formErrors.name ? '#E74C3C' : 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                  />
                  {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                </div>

                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full px-3 py-2 rounded-lg border bg-gray-100"
                    style={{ color: 'var(--muted)' }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--panel-bg)',
                      borderColor: 'var(--panel-border)',
                      color: 'var(--text)'
                    }}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{ 
                    borderColor: 'var(--panel-border)',
                    color: 'var(--text)'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitEdit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 rounded-lg font-medium"
                  style={{ 
                    backgroundColor: 'var(--g4g-accent)',
                    color: 'white'
                  }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
